/**
 * gamepad-core — pure gamepad input math for controller support.
 * No DOM, no React; consumers apply dead-zone/curve to the raw frame.
 */

export interface ControllerMapping {
  deadmanButton: number
  /** Legacy CCTV knob — superseded by `cctv` but still honoured (flips the
   *  tilt binding's invert) by `resolveCctvMapping` when `cctv` is absent. */
  invertTilt: boolean
  /** Legacy CCTV knob — superseded by `cctv` but still honoured (moves the
   *  pan/tilt bindings to axes 2/3) by `resolveCctvMapping` when `cctv` is
   *  absent. Also still swaps the named stick fields in `buildFrame`. */
  swapSticks: boolean
  /** Per-user robot control bindings (Phase 5). Absent on legacy stored
   *  mappings — consumers resolve via `resolveRobotMapping`. */
  robot?: RobotControlMapping
  /** Per-user CCTV PTZ axis bindings. Absent on legacy stored mappings —
   *  consumers resolve via `resolveCctvMapping`, which derives the bindings
   *  from the legacy `invertTilt`/`swapSticks` knobs in that case. */
  cctv?: PartialCctvControlMapping
}

/** One movement axis bound to a physical gamepad axis index (RAW, unswapped —
 *  `swapSticks` only remaps the PTZ stick fields, never these bindings). */
export interface RobotAxisBinding {
  axis: number
  invert: boolean
}

export interface RobotControlMapping {
  axes: { vx: RobotAxisBinding; vy: RobotAxisBinding; yaw: RobotAxisBinding }
  /** Discrete robot commands by gamepad button index; an absent key = unbound. */
  buttons: { stand?: number; lie?: number; handshake?: number; twoLeg?: number; estop?: number }
}

/** Defaults reproduce the Phase 4 hard-wired sticks: left stick up = forward
 *  (vx+), left stick left = strafe-left (vy+), right stick right = turn right
 *  (yaw−). estop/handshake/twoLeg ship UNBOUND by design (the user declined a
 *  default e-stop binding — a press that drops the robot must be deliberate). */
export const DEFAULT_ROBOT_MAPPING: RobotControlMapping = {
  axes: {
    vx: { axis: 1, invert: true },
    vy: { axis: 0, invert: true },
    yaw: { axis: 2, invert: true },
  },
  buttons: { stand: 0, lie: 2 },
}

/** Stored preference shape — any level may be missing on a partial save. */
export interface PartialRobotControlMapping {
  axes?: { vx?: Partial<RobotAxisBinding>; vy?: Partial<RobotAxisBinding>; yaw?: Partial<RobotAxisBinding> }
  buttons?: RobotControlMapping['buttons']
}

/**
 * Deep-merge a stored (possibly partial / legacy-absent) robot mapping against
 * `DEFAULT_ROBOT_MAPPING`. Axis bindings merge per-field (an axis must always
 * be bound), but a PRESENT `buttons` object is taken verbatim — the Settings
 * page saves the complete object, so a deliberately cleared binding (key
 * removed) must NOT be resurrected by the default.
 */
export function resolveRobotMapping(robot?: PartialRobotControlMapping): RobotControlMapping {
  return {
    axes: {
      vx: { ...DEFAULT_ROBOT_MAPPING.axes.vx, ...robot?.axes?.vx },
      vy: { ...DEFAULT_ROBOT_MAPPING.axes.vy, ...robot?.axes?.vy },
      yaw: { ...DEFAULT_ROBOT_MAPPING.axes.yaw, ...robot?.axes?.yaw },
    },
    buttons: robot?.buttons ?? { ...DEFAULT_ROBOT_MAPPING.buttons },
  }
}

/** CCTV PTZ axis bindings (RAW, unswapped — like the robot bindings, these
 *  address absolute physical axis indices; `swapSticks` never applies). */
export interface CctvControlMapping {
  axes: { pan: RobotAxisBinding; tilt: RobotAxisBinding }
}

/** Defaults reproduce the hard-wired left stick: stick right = pan right
 *  (axes[0] verbatim), stick up (negative axes[1]) = tilt up — hence tilt
 *  ships inverted. */
export const DEFAULT_CCTV_MAPPING: CctvControlMapping = {
  axes: {
    pan: { axis: 0, invert: false },
    tilt: { axis: 1, invert: true },
  },
}

/** Stored preference shape — any level may be missing on a partial save. */
export interface PartialCctvControlMapping {
  axes?: { pan?: Partial<RobotAxisBinding>; tilt?: Partial<RobotAxisBinding> }
}

/**
 * Resolve the CCTV PTZ bindings from a stored controller mapping.
 *
 * When `mapping.cctv` is PRESENT it deep-merges per-field over
 * `DEFAULT_CCTV_MAPPING` (like `resolveRobotMapping` — an axis must always be
 * bound) and the legacy knobs are ignored. When ABSENT (every pre-existing
 * stored mapping), the bindings are DERIVED from the legacy knobs so stored
 * behavior is preserved exactly: `swapSticks` moves pan/tilt to the right
 * stick's raw axes (2/3 — matching what `buildFrame`'s swapped `leftX`/`leftY`
 * used to feed the PTZ path), and `invertTilt` flips the tilt invert.
 */
export function resolveCctvMapping(mapping: ControllerMapping): CctvControlMapping {
  const cctv = mapping.cctv

  if (cctv) {
    return {
      axes: {
        pan: { ...DEFAULT_CCTV_MAPPING.axes.pan, ...cctv.axes?.pan },
        tilt: { ...DEFAULT_CCTV_MAPPING.axes.tilt, ...cctv.axes?.tilt },
      },
    }
  }

  return {
    axes: {
      pan: { axis: mapping.swapSticks ? 2 : 0, invert: false },
      tilt: { axis: mapping.swapSticks ? 3 : 1, invert: !mapping.invertTilt },
    },
  }
}

export const DEFAULT_CONTROLLER_MAPPING: ControllerMapping = {
  deadmanButton: 5,
  invertTilt: false,
  swapSticks: false,
}

export interface ControllerFrame {
  leftX: number
  leftY: number
  rightX: number
  rightY: number
  /** Raw pad axes copied verbatim from the Gamepad (UNSWAPPED — `swapSticks`
   *  applies only to the four named stick fields above). Optional so frames
   *  hand-built by pre-Phase-5 specs remain type-valid; `buildFrame` always
   *  populates it. */
  axes?: number[]
  buttons: boolean[]
  pressed: number[]
  deadmanHeld: boolean
  timestamp: number
}

export function applyDeadZone(v: number, dz = 0.15): number {
  const mag = Math.abs(v)
  if (mag <= dz) return 0

  return ((mag - dz) / (1 - dz)) * Math.sign(v)
}

export function responseCurve(v: number): number {
  return v * Math.abs(v)
}

export function quantize(v: number, step = 0.05): number {
  return Math.round(v / step) * step
}

export function risingEdges(prev: boolean[], next: boolean[]): number[] {
  const edges: number[] = []

  for (let i = 0; i < next.length; i++) {
    if (next[i] === true && prev[i] !== true) edges.push(i)
  }

  return edges
}

const BUTTON_LABELS = [
  'A',
  'B',
  'X',
  'Y',
  'LB',
  'RB',
  'LT',
  'RT',
  'Back',
  'Start',
  'LS',
  'RS',
  'D-pad Up',
  'D-pad Down',
  'D-pad Left',
  'D-pad Right',
] as const

export function buttonLabel(index: number): string {
  return BUTTON_LABELS[index] ?? `Button ${index}`
}

const AXIS_LABELS = ['Left stick X', 'Left stick Y', 'Right stick X', 'Right stick Y'] as const

export function axisLabel(index: number): string {
  return AXIS_LABELS[index] ?? `Axis ${index}`
}

export function buildFrame(
  gp: Pick<Gamepad, 'axes' | 'buttons' | 'timestamp'>,
  prevButtons: boolean[],
  mapping: ControllerMapping,
): ControllerFrame {
  const axis = (i: number): number => gp.axes[i] ?? 0
  const buttons = gp.buttons.map(b => b.pressed)
  const swap = mapping.swapSticks

  return {
    leftX: swap ? axis(2) : axis(0),
    leftY: swap ? axis(3) : axis(1),
    rightX: swap ? axis(0) : axis(2),
    rightY: swap ? axis(1) : axis(3),
    axes: gp.axes ? [...gp.axes] : [],
    buttons,
    pressed: risingEdges(prevButtons, buttons),
    deadmanHeld: buttons[mapping.deadmanButton] === true,
    timestamp: gp.timestamp,
  }
}
